// Where a campaign is on the way to serving, as a live vertical sequence.
//
// Two densities, one component and one data source: `dense` is the 340px
// sidebar beside the forecast, and the roomy form is the publish modal. They
// were separate before — a modal with a single opaque status line, and a
// sidebar paragraph that said the same thing for every campaign forever — which
// meant the thing you watch while working and the thing you watch after
// pressing Launch could disagree.
//
// It renders on `styles/launch-progress.css`, which has shipped and been
// imported by `main.tsx` since the design port, styling nothing at all. The
// component it was written for was a mock driven by fabricated timings and has
// been deleted; the stylesheet is genuinely good, so the look survives its
// subject. Only `.lp-dense` here is new — the original is sized for a 720px
// modal.
//
// Every value comes from `lib/campaign-publish-sequence`, which is pure and
// tested. This file is markup.

import { useEffect, useState } from 'react';
import { FailureNotice } from '@/components/failure-notice';
import {
  formatElapsed,
  formatEstimate,
  type PublishStep,
  type PublishStepState,
} from '@/lib/campaign-publish-sequence';

const INDICATOR: Record<PublishStepState, string> = {
  done: 'lp-ind-done',
  active: 'lp-ind-active',
  failed: 'lp-ind-failed',
  // A blocked step is waiting on work elsewhere, not broken — it reads as
  // pending, and its reason carries the difference.
  blocked: 'lp-ind-pending',
  pending: 'lp-ind-pending',
  // A note gates nothing; it is drawn as information, never as waiting.
  note: 'lp-ind-note',
};

/**
 * One clock for the whole list.
 *
 * A running step's elapsed time has to tick, and giving each step its own
 * interval would put N timers on screen to render one shared instant.
 */
function useNowMs(active: boolean, periodMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), periodMs);
    return () => clearInterval(timer);
  }, [active, periodMs]);
  return now;
}

function Check() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

function Cross() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="lp-spin" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" aria-hidden="true">
      <path d="M8 1.5a6.5 6.5 0 1 1-6.5 6.5" />
    </svg>
  );
}

/**
 * What each state is called, for anyone not reading the colour.
 *
 * The glyphs are `aria-hidden` and the rest of the state lived only in CSS
 * class names, so before this a screen reader got the step's label and nothing
 * at all about whether it was finished, running or stuck — while `FAILED` was
 * the one state that happened to be spelled out, in a pill.
 */
const STATE_LABEL: Record<PublishStepState, string> = {
  done: 'done',
  active: 'in progress',
  failed: 'failed',
  blocked: 'waiting',
  pending: 'not started',
  note: 'note',
};

function Indicator({ state }: { state: PublishStepState }) {
  return (
    <div className="lp-indicator">
      <div className={`lp-ind ${INDICATOR[state]}`} role="img" aria-label={STATE_LABEL[state]}>
        {state === 'done' && <Check />}
        {state === 'failed' && <Cross />}
        {state === 'active' && <Spinner />}
        {state === 'note' && <span aria-hidden="true">i</span>}
      </div>
    </div>
  );
}

interface StepRowProps {
  step: PublishStep;
  index: number;
  total: number;
  nowMs: number;
  dense: boolean;
  onRetry?: (step: PublishStep) => void;
}

function StepRow({ step, index, total, nowMs, dense, onRetry }: StepRowProps) {
  const [open, setOpen] = useState(false);
  const elapsed = formatElapsed(step, nowMs);
  const estimate = formatEstimate(step);
  const expandable = (step.children?.length ?? 0) > 0;
  const running = step.state === 'active';

  return (
    <div className={`lp-step lp-step-${step.state}`}>
      <div className="lp-gutter">
        {index < total - 1 && (
          <div className="lp-connector">
            <span className={`lp-line${step.state === 'done' ? ' is-done' : ''}`} />
          </div>
        )}
        <Indicator state={step.state} />
      </div>
      <div className="lp-body">
        <div className="lp-row1">
          <div className="lp-label">
            {!dense && <span className="lp-num">{String(index + 1).padStart(2, '0')}</span>}
            <span className="lp-title">{step.label}</span>
          </div>
          <div className="lp-meta">
            {step.count && (
              <span className="lp-elapsed">
                {step.count.done} of {step.count.total}
              </span>
            )}
            {/* `aria-hidden` while it ticks: a value that changes every second
                inside a live region interrupts a screen reader continuously,
                and the elapsed time is decorative — the state is announced by
                the indicator and the duration is re-readable on demand. Once
                the step settles it stops moving and can be read normally. */}
            {elapsed && (
              <span
                className={running ? 'lp-elapsed-live' : 'lp-elapsed'}
                aria-hidden={running || undefined}
              >
                {elapsed}
              </span>
            )}
            {step.state === 'failed' && <span className="lp-failed-pill">FAILED</span>}
          </div>
        </div>

        {step.detail && <div className="lp-subline">{step.detail}</div>}

        {/* Absent unless the server had five single-attempt samples. Never a
            placeholder: "no estimate" and "instant" must not look alike. */}
        {estimate && <div className="lp-subline">{estimate}</div>}

        {/* The failure in the server's words — what happened, what SAM did,
            what to do — with the stored string under "Technical details". A
            step with no words (an older response, or a string the sequence
            made up) shows its text as before. */}
        {step.failure && (
          <div className="lp-explainer is-failed">
            <FailureNotice
              announce={false}
              dense={dense}
              failure={step.failureView}
              fallback={step.failure}
              onRetry={onRetry ? () => onRetry(step) : undefined}
            />
          </div>
        )}

        {expandable && (
          <button type="button" className="lp-subline"
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                  aria-expanded={open}
                  onClick={() => setOpen((value) => !value)}>
            {open ? '− hide' : `+ show ${step.children?.length} detail`}
          </button>
        )}
        {open &&
          step.children?.map((child, childIndex) => (
            <div key={`${child.label}-${childIndex}`} className="lp-subline" style={{ paddingLeft: 12 }}>
              <span style={{ opacity: child.state === 'done' ? 0.7 : 1 }}>
                {child.state === 'done' ? '✓' : child.state === 'failed' ? '✗' : child.state === 'note' ? 'i' : '·'} {child.label}
              </span>
              {child.providerObjectId && (
                <span className="mono" style={{ marginLeft: 8, opacity: 0.75 }}>
                  {child.providerObjectId}
                </span>
              )}
              {(child.failureView || child.detail) && (
                <div style={{ paddingLeft: 14, opacity: 0.8 }} title={child.failureView ? child.detail : undefined}>
                  {child.failureView?.headline ?? child.detail}
                </div>
              )}
            </div>
          ))}

        {step.evidence && step.evidence.length > 0 && !dense && (
          <div className="lp-subline">
            {step.evidence.map((item) => (
              <span key={item.value} className="mono" style={{ marginRight: 10 }}>
                {item.value}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function PublishSequence({
  steps,
  dense = false,
  onRetry,
}: {
  steps: PublishStep[];
  dense?: boolean;
  onRetry?: (step: PublishStep) => void;
}) {
  // Only tick while something is actually running, so a settled campaign costs
  // no timers at all.
  const nowMs = useNowMs(steps.some((step) => step.state === 'active'));
  if (steps.length === 0) return null;
  // No `aria-live` here. Both mounts already sit inside one — the publish
  // modal's body and the campaign sidebar — and nesting polite regions while a
  // clock ticks inside them means a screen reader re-announces the list every
  // second and never finishes a sentence.
  return (
    <div className={`lp-steps${dense ? ' lp-dense' : ''}`}>
      {steps.map((step, index) => (
        <StepRow
          key={step.key}
          step={step}
          index={index}
          total={steps.length}
          nowMs={nowMs}
          dense={dense}
          onRetry={step.state === 'failed' ? onRetry : undefined}
        />
      ))}
    </div>
  );
}
